<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import CModal from '@/components/CModal.vue';
import CSelect, { type CSelectItem, valueReducer } from '@/components/shared/CSelect';
import type { TeamUser } from '@/typings/user';

const props = defineProps<{
    value: boolean;
    usernames?: TeamUser[];
}>();

const emit = defineEmits<{
    (e: 'input', value: boolean): void;
    (e: 'switch', username: string): void;
}>();

const selectedUsername = ref<string>();

const isOpen = computed({
    get: () => props.value,
    set: (value: boolean) => emit('input', value)
});

const options = computed<CSelectItem[]>(() =>
    (props.usernames || [])
        .map((user) => ({
            value: user.ParentUsername || user.Username,
            label: user.ParentUsername
                ? `${user.Name || user.Username} (${user.ParentUsername})`
                : user.Name || user.Username || ''
        }))
        .sort((a, b) => a.label?.localeCompare(b.label, undefined, { sensitivity: 'base' }))
);

watch(
    () => props.value,
    (isVisible) => {
        if (isVisible) {
            selectedUsername.value = undefined;
        }
    }
);

const onSwitch = () => {
    if (selectedUsername.value) {
        emit('switch', selectedUsername.value);
    }
};
</script>

<template>
    <c-modal
        v-model="isOpen"
        title="Switch Team"
        ok-title="Switch"
        :ok-disabled="!selectedUsername"
        :scrollable="false"
        @ok="onSwitch"
    >
        <c-select
            v-model="selectedUsername"
            label="Team account"
            placeholder="Select an account"
            append-to-body
            searchable
            :options="options"
            :model-transform="valueReducer"
        />
    </c-modal>
</template>
